import { useState } from 'react';

interface NotificationPref {
  id: string;
  label: string;
  description: string;
  icon: string;
  email: boolean;
  sms: boolean;
}

const defaultPrefs: NotificationPref[] = [
  {
    id: 'appointments',
    label: 'Appointment Reminders',
    description: 'Get notified about upcoming consultations',
    icon: 'ri-calendar-check-line',
    email: true,
    sms: true,
  },
  {
    id: 'health_tips',
    label: 'Health Tips & News',
    description: 'Stay informed with cardiovascular health insights',
    icon: 'ri-heart-pulse-line',
    email: true,
    sms: false,
  },
  {
    id: 'promotions',
    label: 'Promotions & Updates',
    description: 'Learn about new services and special offers',
    icon: 'ri-megaphone-line',
    email: false,
    sms: false,
  },
];

const NotificationsSection = () => {
  const [prefs, setPrefs] = useState<NotificationPref[]>(defaultPrefs);
  const [saved, setSaved] = useState(false);

  /** Toggle a specific channel (email / sms) for a given preference */
  const togglePref = (id: string, channel: 'email' | 'sms') => {
    setPrefs((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, [channel]: !p[channel] } : p
      )
    );
    setSaved(false);
  };

  /** Simulate a save action – in a real app you would call an API here */
  const handleSave = () => {
    try {
      // Placeholder for async save logic
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Failed to save notification preferences', err);
      // Optional: show user‑facing error UI
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 md:p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-8 h-8 flex items-center justify-center">
          <i className="ri-notification-3-line text-xl text-[#f04e58]"></i>
        </div>
        <h2 className="text-lg font-semibold text-gray-900">
          Notification Preferences
        </h2>
      </div>

      {saved && (
        <div className="mb-6 p-3 rounded-lg text-sm bg-green-50 text-green-700 border border-green-200">
          <div className="flex items-center gap-2">
            <i className="ri-check-line"></i>
            Notification preferences saved!
          </div>
        </div>
      )}

      <div className="space-y-1">
        {/* Header */}
        <div className="hidden md:grid grid-cols-[1fr_80px_80px] gap-4 pb-3 border-b border-gray-100">
          <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
            Notification
          </span>
          <span className="text-xs font-medium text-gray-400 uppercase tracking-wider text-center">
            Email
          </span>
          <span className="text-xs font-medium text-gray-400 uppercase tracking-wider text-center">
            SMS
          </span>
        </div>

        {prefs.map((pref) => (
          <div
            key={pref.id}
            className="grid grid-cols-1 md:grid-cols-[1fr_80px_80px] gap-4 py-4 border-b border-gray-50 last:border-0 items-center"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-[#fef5f5] flex items-center justify-center flex-shrink-0">
                <i className={`${pref.icon} text-[#f04e58] text-lg`}></i>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{pref.label}</p>
                <p className="text-xs text-gray-500">{pref.description}</p>
              </div>
            </div>

            {/* Email toggle */}
            <div className="flex md:justify-center items-center gap-2 md:gap-0">
              <span className="text-xs text-gray-400 md:hidden">Email</span>
              <button
                type="button"
                onClick={() => togglePref(pref.id, 'email')}
                className={`relative w-10 h-6 rounded-full transition-colors cursor-pointer ${
                  pref.email ? 'bg-[#f04e58]' : 'bg-gray-200'
                }`}
                aria-pressed={pref.email}
                aria-label={`Toggle email for ${pref.label}`}
              >
                <span
                  className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform shadow-sm ${
                    pref.email ? 'translate-x-4' : 'translate-x-0'
                  }`}
                ></span>
              </button>
            </div>

            {/* SMS toggle */}
            <div className="flex md:justify-center items-center gap-2 md:gap-0">
              <span className="text-xs text-gray-400 md:hidden">SMS</span>
              <button
                type="button"
                onClick={() => togglePref(pref.id, 'sms')}
                className={`relative w-10 h-6 rounded-full transition-colors cursor-pointer ${
                  pref.sms ? 'bg-[#f04e58]' : 'bg-gray-200'
                }`}
                aria-pressed={pref.sms}
                aria-label={`Toggle SMS for ${pref.label}`}
              >
                <span
                  className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform shadow-sm ${
                    pref.sms ? 'translate-x-4' : 'translate-x-0'
                  }`}
                ></span>
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end pt-4">
        <button
          type="button"
          onClick={handleSave}
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#f04e58] text-white text-sm font-medium rounded-lg hover:bg-[#d93d47] transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-check-line"></i>
          Save Preferences
        </button>
      </div>
    </div>
  );
};

export default NotificationsSection;
